import React from 'react';

import { Box, Button, Typography } from '@mui/material';
import AuditStateMessage from 'app/shared/modules/EntityAuditHistory/components/AuditStateMessage';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  ELSEWHERE_LABEL,
  MARK_SELECT_HINT,
  MARKS_APPROXIMATE_NOTE,
  MARKS_UNAVAILABLE_NOTE,
  NO_MARKS_ON_PAGE_MESSAGE,
  PREVIEW_STATE_MESSAGE,
  TemplateRequestStatus,
  UNPLACED_MARKS_LABEL,
} from '../../FormChangeHistory.constants';
import { PreviewMarksPropType } from '../../FormChangeHistory.propTypes';
import { FormChangeHistoryTestIds } from '../../FormChangeHistory.testIds';
import { getChangesOnPageTitle, getPageChangesLabel } from '../../FormChangeHistory.utils';
import usePageSelect from '../../hooks/usePageSelect';
import PreviewMarkList from '../PreviewMarkList';

import { changesPanelStyles, panelSectionStyles } from './PreviewChangesPanel.styles';

/**
 * The numbered changes on the shown page, the same numbers as on the page, each a toggle that
 * highlights it there; the other pages with changes, each a way to it; and the changes the layout
 * could not place. Waits for both sides.
 */
const PreviewChangesPanel = ({
  pageNumber,
  marksOnPage,
  otherPages,
  unplacedMarks,
  isLayoutMissing,
  onSelectPage,
  onToggleMark,
  highlightedMarkId = null,
  isLoading = false,
}) => {
  const onPageClick = usePageSelect(onSelectPage);

  return (
    <Box data-testid={FormChangeHistoryTestIds.PREVIEW_CHANGES_PANEL} sx={changesPanelStyles}>
      <Typography variant="medium14">{getChangesOnPageTitle(pageNumber)}</Typography>

      {isLoading ? (
        <AuditStateMessage
          isLoading
          dataTestId={FormChangeHistoryTestIds.PREVIEW_CHANGES_STATE}
          message={PREVIEW_STATE_MESSAGE[TemplateRequestStatus.LOADING].message}
        />
      ) : (
        <>
          {_.isEmpty(marksOnPage) ? (
            <Typography color="text.secondary" variant="regular14">
              {NO_MARKS_ON_PAGE_MESSAGE}
            </Typography>
          ) : (
            <>
              <Typography color="text.secondary" variant="regular12">
                {MARK_SELECT_HINT}
              </Typography>
              <PreviewMarkList
                highlightedMarkId={highlightedMarkId}
                marks={marksOnPage}
                onToggleMark={onToggleMark}
              />
            </>
          )}

          {!_.isEmpty(otherPages) && (
            <Box sx={panelSectionStyles}>
              <Typography color="text.secondary" variant="medium12">
                {ELSEWHERE_LABEL}
              </Typography>
              {otherPages.map(page => (
                <Button
                  data-page={page.pageNumber}
                  data-testid={FormChangeHistoryTestIds.PREVIEW_OTHER_PAGE_BUTTON}
                  key={page.pageNumber}
                  variant="line"
                  onClick={onPageClick}
                >
                  {getPageChangesLabel(page.pageNumber, page.count)}
                </Button>
              ))}
            </Box>
          )}

          {!_.isEmpty(unplacedMarks) && (
            <Box
              data-testid={FormChangeHistoryTestIds.PREVIEW_UNPLACED_MARKS}
              sx={panelSectionStyles}
            >
              <Typography color="text.secondary" variant="medium12">
                {UNPLACED_MARKS_LABEL}
              </Typography>
              <PreviewMarkList marks={unplacedMarks} />
            </Box>
          )}

          <Typography color="text.secondary" variant="regular12">
            {isLayoutMissing ? MARKS_UNAVAILABLE_NOTE : MARKS_APPROXIMATE_NOTE}
          </Typography>
        </>
      )}
    </Box>
  );
};

PreviewChangesPanel.propTypes = {
  pageNumber: PropTypes.number.isRequired,
  marksOnPage: PreviewMarksPropType.isRequired,
  otherPages: PropTypes.arrayOf(
    PropTypes.shape({
      pageNumber: PropTypes.number.isRequired,
      count: PropTypes.number.isRequired,
    }),
  ).isRequired,
  unplacedMarks: PreviewMarksPropType.isRequired,
  isLayoutMissing: PropTypes.bool.isRequired,
  onSelectPage: PropTypes.func.isRequired,
  onToggleMark: PropTypes.func.isRequired,
  highlightedMarkId: PropTypes.string,
  isLoading: PropTypes.bool,
};

export default PreviewChangesPanel;
